/**
 * Chunks sob demanda das regiões do AppShell (FE §4.7, §15.7.4 etapa 5; DE-C20): hoje o `probability` (Fase 6); os de
 * `search`, `tv` e `governors` podem usar o mesmo apoio. Cada um é pedido uma vez por `import()` (uma falha libera a
 * nova tentativa) e publicado num sinal, lido pelo slot. O mapa tem a sua própria versão em mapSlot.tsx (etapa 2, com
 * a espera do 1º número).
 */
import { type Signal, signal } from '@preact/signals';
import { useEffect, useState } from 'preact/hooks';
import { chunkFailed } from './staleBuild';

export interface LazyChunk<M> {
  /** Módulo carregado (null até o `import()` resolver). */
  readonly module: Signal<M | null>;
  /** Pede o chunk (uma vez); devolve o módulo quando chega. */
  load(): Promise<M>;
}

export function lazyChunk<M>(importer: () => Promise<M>): LazyChunk<M> {
  const module = signal<M | null>(null);
  let pending: Promise<M> | null = null;
  return {
    module,
    load() {
      if (!pending) {
        const p = importer();
        pending = p;
        p.catch(() => {
          if (pending === p) pending = null;
          // o pedaço pode ter sumido num deploy novo ou numa volta (pré-mortem, ORD-F-146)
          chunkFailed();
        });
      }
      return pending.then((m) => {
        if (module.peek() !== m) module.value = m;
        return m;
      });
    },
  };
}

/**
 * O módulo do chunk quando `wanted`; pede o `import()` na primeira vez que for querido. Uma falha vira erro de render
 * (a boundary da região mostra o `OopsCard` e remonta, tentando de novo); sem rede, espera o evento `online` (§10.9),
 * como o mapa.
 */
export function useLazyChunk<M>(chunk: LazyChunk<M>, wanted: boolean): M | null {
  const mod = chunk.module.value;
  const [failure, setFailure] = useState<{ err: unknown } | null>(null);
  if (failure) throw failure.err;
  useEffect(() => {
    if (!wanted || chunk.module.peek()) return;
    let alive = true;
    let offOnline: (() => void) | null = null;
    const start = () => {
      chunk.load().catch((err: unknown) => {
        if (!alive) return;
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          const onOnline = () => {
            offOnline?.();
            offOnline = null;
            start();
          };
          addEventListener('online', onOnline);
          offOnline = () => removeEventListener('online', onOnline);
          return;
        }
        setFailure({ err });
      });
    };
    start();
    return () => {
      alive = false;
      offOnline?.();
    };
  }, [wanted]);
  return wanted ? mod : null;
}
